import React, { createContext, useContext, useEffect, useState } from 'react';
import {
  type FontScalePreset,
  FONT_SCALE_VALUES,
  parseFontScalePreset,
} from '@/constants/fontScale';
import { applySystemFavicon } from '@/lib/favicon';
import { THEME_LOCKED_FOR_PILOT } from '@/config/pilot';

type Theme = 'dark' | 'light' | 'soft-light' | 'system';

interface ThemeContextType {
  theme: Theme;
  setTheme: (theme: Theme) => void;
  fontScale: FontScalePreset;
  setFontScale: (scale: FontScalePreset) => void;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

export function ThemeProvider({ children }: { children: React.ReactNode }) {
  const [theme, setTheme] = useState<Theme>(() => {
    if (THEME_LOCKED_FOR_PILOT) return 'dark';
    const saved = localStorage.getItem('theme') as Theme;
    return saved || 'system';
  });

  const [fontScale, setFontScale] = useState<FontScalePreset>(() =>
    parseFontScalePreset(localStorage.getItem('fontScale')),
  );

  useEffect(() => {
    const root = window.document.documentElement;

    root.classList.remove('light', 'soft-light', 'dark');

    if (theme === 'system') {
      const systemTheme = window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
      root.classList.add(systemTheme);
    } else {
      root.classList.add(theme);
    }

    // While locked, do not persist: the user's saved preference must survive
    // the pilot so unlocking restores it.
    if (!THEME_LOCKED_FOR_PILOT) {
      localStorage.setItem('theme', theme);
    }
  }, [theme]);

  useEffect(() => {
    const root = window.document.documentElement;
    root.style.setProperty('--font-scale', String(FONT_SCALE_VALUES[fontScale]));
    localStorage.setItem('fontScale', fontScale);
  }, [fontScale]);

  // Favicon follows OS color scheme only (not in-app theme).
  useEffect(() => {
    applySystemFavicon();

    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    const handleChange = () => applySystemFavicon();
    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, []);

  // Sync app classes when OS theme changes and app theme is "system"
  useEffect(() => {
    if (theme !== 'system') return;

    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    const handleChange = (e: MediaQueryListEvent) => {
      const root = window.document.documentElement;
      root.classList.remove('light', 'soft-light', 'dark');
      root.classList.add(e.matches ? 'dark' : 'light');
    };

    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, [theme]);

  return (
    <ThemeContext.Provider value={{ theme, setTheme, fontScale, setFontScale }}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme() {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
}
